import { makeAutoObservable, runInAction } from "mobx";
import React, { useContext } from "react";
import { CraNodeCoding } from "../../coding/models/CraNodeCoding";
import { FeatureCollection } from "geojson";
import {
  parseAttributeInputValue,
  setAttributeByName,
} from "../../coding/utils/attribute-value";
import {
  CraAttributes,
  CraAttributeName,
  DistanceUnit,
  IScore,
  Score,
} from "@keita/lanepatrol-sdk";
import {
  CreateCraForecastScenarioRequest,
  CraForecastAction,
  CraForecastChangeType,
  CraForecastScenario,
  CraSimulatedResult,
  UpdateCraForecastScenarioRequest,
  CraForecastFilters,
  CraForecastChange,
} from "../models/CraForecastScenario";
import { RootStore } from "../../shared/stores/RootStore";
import { CraNode } from "../../cra/models/CraNode";
import { SurveyApi } from "../services/SurveyApi";
import {
  flattenBidimensionalMap,
  unflattenBidimensionalMap,
} from "../../shared/utils/object-utils";
import {
  scoreFromValues,
  scoreToGeoJsonNodeScore,
} from "../../coding/utils/score-utils";
import { CraNodeCodingApi } from "../../coding/services/CraNodeCodingApi";
import { applyTreatmentToCraNodeCoding } from "../utils/treatment-changes";
import {
  isNodeVisibleByAttributes,
  isVisibleByScore,
  isNodeVisibleByTreatment,
  isCraWayVisibleThroughFilters,
} from "../utils/apply-filters";
import { LifeCycleStage } from "../../cra/models/LifeCycleStage";
import { CraWay } from "../../cra/models/CraWay";
import { CraForecastApi } from "../services/CraForecastApi";

type craWayId = string;
type craNodeId = string;

export class CraForecastStore {
  rootStore;
  actions: CraForecastAction[] = [];
  computedValues?: {
    allNodeCodingsByWayId: Map<craWayId, Map<craNodeId, CraNodeCoding>>;
    craNodes: Map<craWayId, CraNode[]>;
    craWays: CraWay[];
  };
  scenarios: CraForecastScenario[] = [];
  selectedScenario?: CraForecastScenario;
  computingValues = false;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
  }

  resetState() {
    this.restoreOriginal();
    runInAction(() => {
      this.scenarios = [];
      this.computingValues = false;
    });
  }

  restoreOriginal() {
    runInAction(() => {
      this.actions = [];
      this.computedValues = undefined;
      this.selectedScenario = undefined;
    });
  }

  isForecastModeActivated() {
    return !!this.actions.length;
  }

  addAction(forecastChange: CraForecastChange) {
    const { filterStore } = this.rootStore;
    runInAction(() => {
      this.actions = this.actions.concat([
        {
          change: forecastChange,
          filters: {
            craWayFilters: filterStore.craWayFilters,
            scoreFilters: filterStore.craScoreFilters,
            treatmentFilters: filterStore.treatmentFilters,
            attributeFilters: filterStore.craAttributeFilters,
          },
        },
      ]);
    });
    this.computeValues();
  }

  removeActionByIndex(index: number) {
    runInAction(() => {
      this.actions = this.actions
        .filter((_, i) => i !== index)
        .map((action, i) =>
          i < index ? action : { ...action, results: undefined },
        );
    });
    this.computeValues();
  }

  setSelectedScenario(scenarioId: string | undefined) {
    const forecastScenario = this.scenarios.find(
      (scenario) => scenario.id === scenarioId,
    );

    if (forecastScenario) {
      runInAction(() => {
        this.selectedScenario = forecastScenario;
        this.actions = forecastScenario.actions;
      });
    } else {
      this.restoreOriginal();
    }

    this.computeValues();
  }

  async createForecastScenario(request: CreateCraForecastScenarioRequest) {
    const response = await CraForecastApi.create(request);
    runInAction(() => {
      this.scenarios = [...this.scenarios, response.data];
      this.selectedScenario = response.data;
    });
    return response;
  }

  async updateForecastScenario(
    id: string,
    request: UpdateCraForecastScenarioRequest,
  ) {
    const response = await CraForecastApi.update(id, request);
    runInAction(() => {
      this.scenarios = this.scenarios.map((item) =>
        item.id === id ? response.data : item,
      );
      this.selectedScenario = response.data;
    });
    return response;
  }

  async deleteForecastScenario(id: string) {
    await CraForecastApi.delete(id);
    runInAction(() => {
      this.scenarios = this.scenarios.filter((item) => item.id !== id);
      this.selectedScenario = undefined;
    });
  }

  async fetchScenarios(surveyId: string, cancelIf?: () => boolean) {
    const response = await SurveyApi.listCraForecastScenarios(surveyId);
    if (cancelIf?.()) return;

    runInAction(() => {
      this.scenarios = response.data;
    });
  }

  private async computeValues() {
    const { surveyStore, filterStore, craNodeStore, codingStore } =
      this.rootStore;

    if (!this.isForecastModeActivated()) {
      runInAction(() => {
        this.computedValues = undefined;
      });
      return;
    }

    filterStore.forceCodedLifeCycleStageFilters(
      filterStore.lifeCycleStageFilters,
    );

    runInAction(() => {
      this.computingValues = true;
    });

    try {
      const originalCraWays = filterStore.craWaysAvailableToUser;
      const originalCodingEntries = codingStore.allNodeCodingsByWayId;
      const originalCraNodes = craNodeStore.nodesByWayId;

      const { forecastedCraWays, forecastedNodes, forecastedCodingEntries } =
        flattenOriginalData(
          originalCraWays,
          originalCraNodes,
          originalCodingEntries,
        );

      for (const action of this.actions) {
        const affectedKeyPairs = filterAffectedNodes(
          forecastedNodes,
          forecastedCodingEntries,
          action.filters,
        );

        changeForecastedCodingEntries(
          action,
          affectedKeyPairs,
          forecastedCodingEntries,
          forecastedNodes,
        );

        if (!action.results) {
          const affectedCodingEntries = new Map(
            affectedKeyPairs.map(([craWayId, craNodeId]) => [
              `${craWayId},${craNodeId}`,
              forecastedCodingEntries.get(`${craWayId},${craNodeId}`)!,
            ]),
          );

          const response = await simulateChangeResults(
            affectedCodingEntries,
            surveyStore.distanceUnit,
          );
          runInAction(() => {
            action.results = response;
          });
        }

        applyComputedValuesToForecastedData(
          action.results!,
          forecastedCraWays,
          forecastedNodes,
        );
      }

      runInAction(() => {
        this.computedValues = unflattenForecastedData(
          forecastedCraWays,
          forecastedNodes,
          forecastedCodingEntries,
        );
      });
    } catch (error) {
      console.error(error);
    } finally {
      runInAction(() => {
        this.computingValues = false;
      });
    }
  }
}

interface ContextProps {
  craForecastStore: CraForecastStore;
}
export const CraForecastStoreContext = React.createContext({} as ContextProps);

export const useCraForecastStoreContext = () =>
  useContext(CraForecastStoreContext);

function flattenOriginalData(
  originalCraWays: CraWay[],
  originalNodes: Map<craWayId, CraNode[]>,
  originalCodingEntries: Map<craWayId, Map<craNodeId, CraNodeCoding>>,
) {
  const filterCodedWays = (way: CraWay) =>
    way.lifeCycleStage === LifeCycleStage.Review ||
    way.lifeCycleStage === LifeCycleStage.Completed;

  const forecastedCraWays = new Map<craWayId, CraWay>(
    originalCraWays
      .filter(filterCodedWays)
      .map((way) => [way.id, JSON.parse(JSON.stringify(way))]),
  );

  const forecastedNodes = new Map<craWayId, CraNode>(
    [...originalNodes.entries()].flatMap(([craWayId, nodes]) => {
      if (!forecastedCraWays.has(craWayId)) return [];
      return nodes.map((node) => [node.id, JSON.parse(JSON.stringify(node))]);
    }),
  );

  const forecastedCodingEntries = flattenBidimensionalMap(
    originalCodingEntries,
    (entry) => JSON.parse(JSON.stringify(entry)),
  );

  return { forecastedCraWays, forecastedNodes, forecastedCodingEntries };
}

function unflattenForecastedData(
  forecastedCraWays: Map<craWayId, CraWay>,
  forecastedNodes: Map<craNodeId, CraNode>,
  forecastedCodingEntries: Map<string, CraNodeCoding>,
) {
  const craWays: CraWay[] = [...forecastedCraWays.values()];

  const craNodes = new Map<craWayId, CraNode[]>();
  for (const node of forecastedNodes.values()) {
    const nodeList = craNodes.get(node.craWayId);
    if (nodeList) {
      nodeList.push(node);
    } else {
      craNodes.set(node.craWayId, [node]);
    }
  }

  const allNodeCodingsByWayId = unflattenBidimensionalMap(
    forecastedCodingEntries,
  );

  return { craWays, craNodes, allNodeCodingsByWayId };
}

function applyComputedValuesToForecastedData(
  simulatedResults: Map<string, CraSimulatedResult>,
  forecastedCraWays: Map<string, CraWay>,
  forecastedNodes: Map<string, CraNode>,
) {
  const scoresByRouteId = new Map<string, (Score | null)[]>();

  for (const [keys, simulatedResult] of simulatedResults) {
    const [craWayId, craNodeId] = keys.split(",");

    const node = forecastedNodes.get(craNodeId)!;
    forecastedNodes.set(craNodeId, {
      ...node,
      score: simulatedResult.score,
      scoreImpact: simulatedResult.scoreImpact,
      treatments: simulatedResult.treatments,
    });

    // update road with new section score
    const way = forecastedCraWays.get(craWayId)!;
    if (way.geoJson) {
      const geoJson: FeatureCollection = {
        ...way.geoJson,
        features: way.geoJson.features.map((feature) => {
          if (feature.properties?.id === craNodeId) {
            return {
              ...feature,
              properties: {
                ...feature.properties,
                color: simulatedResult.score.global.color,
                ...scoreToGeoJsonNodeScore(simulatedResult.score),
              },
            };
          } else {
            return feature;
          }
        }),
      };
      way.geoJson = geoJson;
    }

    scoresByRouteId.set(
      craWayId,
      (scoresByRouteId.get(craWayId) ?? []).concat([simulatedResult.score]),
    );
  }

  // re-calculate way scores, by averaging out node scores
  for (const [craWayId, forecastedScores] of scoresByRouteId) {
    const way = forecastedCraWays.get(craWayId)!;
    const totalScores = forecastedScores.length;

    if (way?.score) {
      // if original score is null, there wont be a forecasted score either
      const routeScoreValues = forecastedScores.reduce(
        (scoreSum, score) => {
          scoreSum.global += score!.global.value / totalScores;
          scoreSum.bicycleToBicycle +=
            score!.bicycleToBicycle.value / totalScores;
          scoreSum.bicycleToPedestrian +=
            score!.bicycleToPedestrian.value / totalScores;
          scoreSum.vehicleToBicycle +=
            score!.vehicleToBicycle.value / totalScores;
          scoreSum.singleBicycle += score!.singleBicycle.value / totalScores;

          return scoreSum;
        },
        {
          global: 0,
          bicycleToBicycle: 0,
          bicycleToPedestrian: 0,
          vehicleToBicycle: 0,
          singleBicycle: 0,
        } as IScore<number>,
      );

      way.score = scoreFromValues(routeScoreValues);
    }
  }
}

async function simulateChangeResults(
  flatAffectedCodingEntries: Map<string, CraNodeCoding>,
  surveyDistanceUnit: DistanceUnit,
): Promise<Map<string, CraSimulatedResult>> {
  const affectedKeyPairs = [...flatAffectedCodingEntries.keys()];
  const affectedCodingEntries = [...flatAffectedCodingEntries.values()];

  const chunkSize = 500;
  var allResults: CraSimulatedResult[] = [];

  for (let i = 0; i < affectedCodingEntries.length; i += chunkSize) {
    const chunk = affectedCodingEntries.slice(i, i + chunkSize);

    const response = await CraNodeCodingApi.simulateResults({
      distanceUnit: surveyDistanceUnit,
      attributes: chunk.map((ref) => ref.attributes),
    });

    allResults = allResults.concat(response.data);
  }

  return new Map(
    allResults.map((simulatedResult, i) => [
      affectedKeyPairs[i],
      simulatedResult,
    ]),
  );
}

function changeForecastedCodingEntries(
  action: CraForecastAction,
  affectedKeyPairs: [craWayId, craNodeId][],
  forecastedCodingEntries: Map<string, CraNodeCoding>,
  forecastedNodes: Map<string, CraNode>,
) {
  switch (action.change.type) {
    case CraForecastChangeType.AttributeValues: {
      for (const [craWayId, craNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${craWayId},${craNodeId}`,
        )!;

        for (const [attributeName, stringValue] of Object.entries(
          action.change.attributeValues,
        ) as [CraAttributeName, string][]) {
          const attributeDefinition =
            CraAttributes.getByName(attributeName)!;
          const value = parseAttributeInputValue(
            attributeDefinition,
            stringValue,
          );
          codingEntry.attributes = setAttributeByName(
            codingEntry.attributes,
            attributeName,
            value,
          );
        }
      }
      break;
    }

    case CraForecastChangeType.HighestPriorityTreatment: {
      for (const [craWayId, craNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${craWayId},${craNodeId}`,
        )!;

        const node = forecastedNodes.get(craNodeId)!;

        if (node.treatments?.length) {
          const [highestPriorityTreatment] = node.treatments;
          applyTreatmentToCraNodeCoding(
            highestPriorityTreatment.type,
            codingEntry,
          );
        }
      }
      break;
    }

    case CraForecastChangeType.TreatmentWhenSuggested: {
      for (const [craWayId, craNodeId] of affectedKeyPairs) {
        const codingEntry = forecastedCodingEntries.get(
          `${craWayId},${craNodeId}`,
        )!;

        const node = forecastedNodes.get(craNodeId)!;
        const treatmentType = action.change.treatmentType;

        if (node.treatments?.find((t) => t.type === treatmentType)) {
          applyTreatmentToCraNodeCoding(treatmentType, codingEntry);
        }
      }
      break;
    }

    default: {
      console.warn("Change not implemented yet.");
    }
  }
}

function filterAffectedNodes(
  forecastedNodes: Map<craNodeId, CraNode>,
  forecastedCodingEntries: Map<string, CraNodeCoding>,
  filters: CraForecastFilters,
) {
  const affectedNodes: [craWayId, craNodeId][] = [];

  for (const node of forecastedNodes.values()) {
    const codingEntry = forecastedCodingEntries.get(
      `${node.craWayId},${node.id}`,
    )!;
    if (
      isCraWayVisibleThroughFilters(node.craWayId, filters.craWayFilters) &&
      isNodeVisibleByAttributes(
        codingEntry.attributes,
        filters.attributeFilters,
      ) &&
      isVisibleByScore(node.score!, filters.scoreFilters) &&
      isNodeVisibleByTreatment(node.treatments, filters.treatmentFilters)
    ) {
      affectedNodes.push([node.craWayId, node.id]);
    }
  }

  return affectedNodes;
}
